import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import type { KpiPeriod } from "@/features/kpi/periodFilter";

/**
 * Kuyruk KPI donem filtresi — eski arayuzdeki ust bar `#periodSel`
 * (Bugün / Son 7 Gün / Son 30 Gün / Tüm Zamanlar; legacy js/kpi.js
 * `localStorage['vdd.kpi.period']`) portu. Gidisat donemi (7/30/90 gun,
 * `usePeriod`) ile AYRI tutulur: ozet paketi `/ozet?days=` yalniz 7/30/90
 * kabul eder, kuyruk kartlari ise bugun/tumu de ister (kullanici bulgusu
 * 10.09.2026: "Bugün filtresi yok").
 */
export const KPI_PERIODS: KpiPeriod[] = ["today", 7, 30, "all"];
const STORAGE_KEY = "asakai.kpi.period";
const DEFAULT_PERIOD: KpiPeriod = 30; // legacy: `<option value="month" selected>`

function isKpiPeriod(value: unknown): value is KpiPeriod {
  return value === "today" || value === "all" || value === 7 || value === 30 || value === 90;
}

function readStored(): KpiPeriod {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_PERIOD;
    const parsed: unknown = raw === "today" || raw === "all" ? raw : Number(raw);
    return isKpiPeriod(parsed) ? parsed : DEFAULT_PERIOD;
  } catch {
    return DEFAULT_PERIOD;
  }
}

function writeStored(value: KpiPeriod): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // yut — tercih hatirlama sadece kolaylik, yoklugu islevi bozmaz.
  }
}

interface KpiPeriodContextValue {
  kpiPeriod: KpiPeriod;
  setKpiPeriod: (period: KpiPeriod) => void;
}

const KpiPeriodContext = createContext<KpiPeriodContextValue | undefined>(undefined);

export function KpiPeriodProvider({ children, defaultPeriod }: { children: ReactNode; defaultPeriod?: KpiPeriod }) {
  const [kpiPeriod, setState] = useState<KpiPeriod>(() => defaultPeriod ?? readStored());
  const setKpiPeriod = useCallback((period: KpiPeriod) => {
    setState(period);
    writeStored(period);
  }, []);
  const value = useMemo<KpiPeriodContextValue>(() => ({ kpiPeriod, setKpiPeriod }), [kpiPeriod, setKpiPeriod]);
  return <KpiPeriodContext.Provider value={value}>{children}</KpiPeriodContext.Provider>;
}

/** Provider yoksa (testler, gomulu ozet) varsayilan donemle salt-okunur calisir. */
export function useKpiPeriod(): KpiPeriodContextValue {
  const ctx = useContext(KpiPeriodContext);
  if (ctx) return ctx;
  return { kpiPeriod: DEFAULT_PERIOD, setKpiPeriod: () => undefined };
}
